import { Body2, Caption } from 'components/shareable/Typography';
import UpgradeToProChip from 'components/shareable/UpgradeToProChip';
import { CurrentBrand } from "modules/Types";

import Colors from 'modules/Colors';
import { PlanType, UserPermissionOnBrand } from 'modules/Const';
import { clsx, styx } from 'modules/Utils';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import { CSSProperties, useState } from 'react';
import TagSymphony, { TagColors } from 'elements/TagSymphony';
import InputSymphony from 'elements/InputSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import BrandInfo from 'elements/BrandInfo';

const PERMISSION_CONFIG: Record<UserPermissionOnBrand, { title: string; color: TagColors; description: string }> = {
    [UserPermissionOnBrand.OWNER]: {
        title: 'Owner',
        color: TagColors.purple,
        description: 'Full ownership and control of the brand'
    },
    [UserPermissionOnBrand.ADMINISTRATOR]: {
        title: 'Admin',
        color: TagColors.green,
        description: 'Full access to create and edit campaigns & websites'
    },
    [UserPermissionOnBrand.VIEW_ONLY]: {
        title: 'View Only',
        color: TagColors.yellow,
        description: 'View-only access, cannot edit or create campaigns'
    },
    [UserPermissionOnBrand.NONE]: {
        title: 'None',
        color: TagColors.grey,
        description: 'No access to this brand'
    }
};

const PERMISSION_OPTIONS = [
    { value: UserPermissionOnBrand.ADMINISTRATOR, title: 'Admin' },
    { value: UserPermissionOnBrand.VIEW_ONLY, title: 'View Only' },
    { value: UserPermissionOnBrand.NONE, title: 'None' }
];

interface BrandsListProps {
    brands: CurrentBrand[];
    canEdit?: boolean;
    isCurrentUser?: boolean;
    onUpdateBrands?: (artists: CurrentBrand[]) => void;
}

const BrandsList = ({
    brands, // NOTE: this is a "temp limited brands list" because it might come from TeamMember.brands, which is an array of brand.ids and limited properties AND we are editing properties like permissionLevel
    canEdit = true,
    isCurrentUser = false,
    onUpdateBrands = () => { },
}: BrandsListProps) => {
    const { organization } = useOrganizationV2();
    const [searchText, setSearchText] = useState('');

    const onUpdatePermission = (id: number, permission: UserPermissionOnBrand) => {
        // Always update permission, don't remove from list
        const updatedBrands = brands.map(brand =>
            brand.id === id ? { ...brand, permissionLevel: permission } : brand
        );
        onUpdateBrands(updatedBrands);
    };

    const setBulkPermission = (permission: UserPermissionOnBrand) => {
        const updatedBrands = brands.map(brand => ({ ...brand, permissionLevel: permission }));
        onUpdateBrands(updatedBrands);
    };

    // Filter brands based on search
    const filteredBrands = brands.filter(brand =>
        brand.name.toLowerCase().includes(searchText.toLowerCase())
    );

    const renderBrandRow = (brand: CurrentBrand) => {
        // Get complete brand data from organization.brands (includes images, tier, etc.)
        const organizationBrand = organization?.brands?.find(b => b.id === brand.id);
        const brandData = organizationBrand || brand; // Fallback to passed brand if not found
        const currentPermission = brand.permissionLevel;

        return (
            <div key={brand.id} className="brandRow" style={sty.brandRow}>
                <div className="brandRowLeft" style={sty.brandRowLeft}>
                    <BrandInfo
                        className="brandInfo"
                        brandName={brandData.name}
                        brandImage={brandData?.image || ''}
                        brandSlug={brandData.slug}
                        variant="default"
                        titleColor={Colors.black}
                        showSlug={false}
                    />
                    <div className="brandRowDetails" style={sty.brandRowDetails}>
                        {brandData.tier === PlanType.PRO && (
                            <UpgradeToProChip
                                className="brandProChip"
                                type="label"
                                withMargin={false}
                            />
                        )}
                    </div>
                </div>
                <div className="brandRowRight" style={sty.brandRowRight}>
                    {canEdit ? (
                        <InputSymphony
                            className="brandPermissionSelect"
                            value={currentPermission}
                            onChange={(value) => onUpdatePermission(brand.id, value as UserPermissionOnBrand)}
                            isSelectDropdown={true}
                            options={PERMISSION_OPTIONS}
                            placeholder="Select permission"
                            style={{ minWidth: 140 }}
                        />
                    ) : (
                        <div className="brandPermissionReadOnly" style={sty.brandPermissionReadOnly}>
                            {PERMISSION_CONFIG[currentPermission].title}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderSearchAndBulkActions = () => {
        if (!canEdit) return null;

        return (
            <div className="searchAndBulkActions" style={sty.searchAndBulkActions}>
                <InputSymphony
                    className="brandSearch"
                    value={searchText}
                    onChange={setSearchText}
                    placeholder="Search brands..."
                    isSearch={true}
                />
                {brands.length > 1 && (
                    <div className="bulkActions" style={sty.bulkActions}>
                        <ButtonSymphony
                            className="bulkActionButton"
                            variant="outlined"
                            size="small"
                            onClick={() => setBulkPermission(UserPermissionOnBrand.ADMINISTRATOR)}
                        >
                            Set All to Admin
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="bulkActionButton"
                            variant="outlined"
                            size="small"
                            onClick={() => setBulkPermission(UserPermissionOnBrand.VIEW_ONLY)}
                        >
                            Set All to View Only
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="bulkActionButton"
                            variant="outlined"
                            size="small"
                            onClick={() => setBulkPermission(UserPermissionOnBrand.NONE)}
                            style={{ color: Colors.red, borderColor: Colors.red }}
                        >
                            Set All to None
                        </ButtonSymphony>
                    </div>
                )}
            </div>
        );
    };

    const renderBrandsAccessList = () => {
        if (!brands.length) {
            return (
                <div className="brandsAccessListContainer" style={sty.emptyState}>
                    <Caption color={Colors.greyDark}>No brands linked.</Caption>
                </div>
            )
        }

        const brandsToShow = searchText ? filteredBrands : brands;

        return (
            <div className="brandsAccessListContainer" style={sty.brandsListContainer}>
                {brandsToShow.map((brand) => renderBrandRow(brand))}
                {searchText && filteredBrands.length === 0 && (
                    <div className="noSearchResults" style={sty.noSearchResults}>
                        <Caption color={Colors.greyDark}>
                            No brands found matching "{searchText}"
                        </Caption>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="brandsListComponent" style={sty.brandsListComponent}>
            <div className="brandsListHeader" style={sty.brandsListHeader}>
                <Body2 color={Colors.black} >
                    {isCurrentUser ?
                        'You have access to:'
                        :
                        'This teammate has access to:'
                    }
                </Body2>
            </div>
            {renderSearchAndBulkActions()}
            {renderBrandsAccessList()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    brandsListComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
    },
    brandsListHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    searchAndBulkActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 16,
    },
    bulkActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    brandsListContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxHeight: 300,
        overflowY: 'auto',
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8,
    },
    brandRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        backgroundColor: Colors.white,
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    brandRowLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    brandRowDetails: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginLeft: 8,
    },
    brandRowRight: {
        display: 'flex',
        alignItems: 'center',
    },
    brandPermissionReadOnly: {
        fontSize: 14,
        color: Colors.greyDark,
        fontWeight: 500,
    },
    emptyState: {
        height: 80,
        backgroundColor: Colors.greyLight,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
    },
    noSearchResults: {
        textAlign: 'center',
        padding: 24,
        backgroundColor: Colors.greyLight,
    },
}

export default BrandsList;